import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Checkbox from 'antd/es/checkbox';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import React, { useCallback, useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { ArtifactBlockConfig, PipelineBlock } from '@mai/types';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface ArtifactBlockEditorProps {
  block: PipelineBlock & { type: 'artifact' };
  onConfigChange: (config: ArtifactBlockConfig) => void;
}

export const ArtifactBlockEditor: React.FC<ArtifactBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      name: block.config.name || 'artifact',
      description: block.config.description || '',
      include_metadata: block.config.include_metadata ?? true,
    });
  }, [block.config, form]);

  // Convert the current JSON schema to a string for the editor with prettification
  const currentSchemaString = React.useMemo(() => {
    const schema = block.config.json_schema;

    // If no schema exists, return empty object
    if (!schema) {
      return '{}';
    }

    try {
      let jsonToStringify;

      // If it's already a string, try to parse it first
      if (typeof schema === 'string') {
        if ((schema as string).trim() === '') {
          return '{}';
        }
        jsonToStringify = JSON.parse(schema);
      } else {
        // It's already an object
        jsonToStringify = schema;
      }

      // If it's an empty object, return formatted empty object
      if (
        typeof jsonToStringify === 'object' &&
        Object.keys(jsonToStringify).length === 0
      ) {
        return '{}';
      }

      // Prettify the JSON
      return JSON.stringify(jsonToStringify, null, 2);
    } catch {
      // If parsing fails, return the original value if it's a string, otherwise empty object
      if (typeof schema === 'string') {
        return schema;
      }
      return '{}';
    }
  }, [block.config.json_schema]);

  const handleFormChange = (
    _: unknown,
    allValues: {
      name?: string;
      description?: string;
      include_metadata?: boolean;
    },
  ) => {
    onConfigChange({
      ...block.config,
      name: allValues.name || 'artifact',
      description: allValues.description || undefined,
      include_metadata: allValues.include_metadata ?? true,
    });
  };

  const handleJsonChange = useCallback(
    (value: string) => {
      // Only update config when JSON is valid
      if (isValid) {
        try {
          const parsedSchema = JSON.parse(value);
          onConfigChange({
            ...block.config,
            json_schema: parsedSchema,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleFormChange}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
          help="Name for this artifact in the pipeline"
        >
          <Input placeholder="artifact" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          help="Optional description of what this artifact contains"
        >
          <Input.TextArea
            placeholder="Enter a description of the artifact"
            rows={2}
          />
        </Form.Item>

        <Form.Item
          name="include_metadata"
          valuePropName="checked"
          help="Include execution metadata (timestamp, etc.) with the artifact"
        >
          <Checkbox>Include Metadata</Checkbox>
        </Form.Item>
      </Form>

      <EditorContainer>
        <EditorLabel>JSON Schema (Optional)</EditorLabel>
        <EditorHelp>
          Optional JSON schema to validate the input data before storing
        </EditorHelp>

        {error && (
          <Alert
            type="error"
            message="Invalid JSON"
            description={error}
            showIcon
          />
        )}

        <JsonEditor
          value={currentSchemaString}
          onChange={handleJsonChange}
          onValidate={handleValidation}
          height="200px"
          placeholder='{"type": "object", "properties": {"data": {"type": "string"}}}'
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </EditorContainer>
    </>
  );
};